/**
 * MobileRecordWizard — the PHONE face of the unified production-record input
 * (plan-06-mobile.md, lane C; `mobile.md`; PROD-1511).
 *
 * The phone workflow, unchanged in its steps:
 *
 *   1. START     two equal choices (PROD-1511): scan the paper sheets, or type
 *                the numbers. Typing is a first-class path, not a small link
 *                under a disabled button; it goes straight to the review.
 *   2. UPLOAD    one upload per paper the school holds (`SheetUploadCard`): the
 *                kitchen's Orders sheet and the production record, each saying
 *                which sheet it wants and what a photo of it fills;
 *   3. READING   the flow's two-paper scan over EVERY unsigned mealtime at
 *                once (`handleScanSheets`);
 *   4. REVIEW    one mealtime at a time: what blocks Confirm listed at the top,
 *                every row as tiles (`RecordTileCard`), a tap opening the
 *                docked keypad on that value (`DockedKeypad`), the step's own
 *                Write-ins / Kitchen / Delivery / Service cards under them.
 *                The main button reads "Fix N things" while anything blocks
 *                and "Confirm Breakfast" once nothing does; confirming moves to
 *                the next unsigned mealtime ("Review Breakfast → Confirm →
 *                Review Lunch → Confirm"), the explicit chain the phone asked
 *                for. The desktop keeps its no-auto-advance rail.
 *
 * OWNS NOTHING BUT ITS OWN PHASE, its photo queue and which value the keypad is
 * on. Every value, handler and gate comes from `RecordSpreadsheetFlow`: the row
 * views are the flow's own (`groupsFor`), the scan is its `handleScanSheets`,
 * the blockers its gate's own facts, the confirm its `handleSignAndComplete`
 * against the ACTIVE step — so on entering a review step the wizard calls
 * `onSelectStep` and the flow's gates and chain follow.
 *
 * NOT A NATIVE `<form>` (ruling E1): Enter must never sign a USDA record.
 * Every button is an explicit `type="button"`.
 */

import { Button } from '@ordo/ui/button'
import { SurfaceCloseButton } from '@ordo/ui/icon-button-close'
import { Skeleton } from '@ordo/ui/skeleton'
import {
  ArrowDown,
  ArrowRight,
  Camera,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Keyboard,
  Loader2,
  type LucideIcon,
} from 'lucide-react'
import { type ReactNode, type RefObject, useEffect, useRef, useState } from 'react'

import { CafeteriaWorkflowHeader } from '@/components/cafeteria/workflow-header'
import { DockedKeypad } from '@/components/production/mobile/docked-keypad'
import { RecordTileCard, TILE_LABEL } from '@/components/production/mobile/record-tile-card'
import { SheetUploadCard } from '@/components/production/sheets/sheet-upload-card'
import { ClaimedMealsCard } from '@/components/production/spreadsheet/claimed-meals-card'
import type {
  RecordColumnKey,
  RecordSpreadsheetGroupView,
  RecordSpreadsheetRowView,
} from '@/components/production/spreadsheet/record-spreadsheet-table'
import { type ScanApplySummary, SCHOOL_SHEET_SCAN_COPY } from '@/components/tasks/sheet-scan-card'
import { presetOptionsForClass, type TempCheckpointClass } from '@/components/tasks/temp-checkpoint-input'
import type { NumericKeypadKey } from '@/lib/cafeteria/numeric-keypad'
import {
  adjacentTile,
  draftTemperature,
  editableTiles,
  type KeypadDraft,
  keypadHint,
  pressKeypadKey,
  type TileRef,
  tileText,
} from '@/lib/production-records/mobile-keypad'
import {
  confirmLabel,
  mealtimesLine,
  mergePhotos,
  MOBILE_WIZARD_PHOTO_CAP,
  nextReviewStep,
  photoKey,
  readingTitle,
  reviewSteps,
  stepCounter,
} from '@/lib/production-records/mobile-wizard'
import {
  deliverySheetNote,
  EMPTY_SHEET_PHOTOS,
  readPhotosLabel,
  recordSheetDetail,
  type SheetPaper,
  sheetPhotoCount,
  type SheetPhotos,
  stepDeliveryDates,
} from '@/lib/production-records/sheet-papers'
import { gradeGroupTitle, type RecordStep } from '@/lib/production-records/spreadsheet-model'
import { blockersTitle, fixLabel, type StepBlocker } from '@/lib/production-records/step-blockers'
import { attributeValue, scrollToMarked } from '@/lib/scroll-to-marked'

export interface MobileRecordWizardProps {
  /** "{day} · {campus}" — the fullscreen host hides the page, so the screen names itself. */
  subtitle?: string
  /** The flow's reads are still on their first fetch. */
  loading: boolean
  /** The flow's chain is running. */
  busy: boolean
  steps: readonly RecordStep[]
  stepDone: (step: RecordStep) => boolean
  /** The flow's active step — the one its gates and chain apply to. */
  activeStepKey: string | null
  onSelectStep: (key: string) => void
  /** The step's rows as the paper lays them out: grade-group blocks → sections → row views, with the claim total (PROD-1441). */
  groupsFor: (step: RecordStep) => readonly RecordSpreadsheetGroupView[]
  /** The flow's two-paper scan: one request per paper over every target. */
  onScan: (photos: SheetPhotos, targets: RecordStep[]) => Promise<ScanApplySummary>
  /** The flow's sign gate for the ACTIVE step. */
  signGateOk: boolean
  /** What keeps the ACTIVE step's gate shut, in words; empty when it is open. */
  blockers: readonly StepBlocker[]
  /** The flow's `Sign & complete` for the active step. Resolves when the chain settled, success or not. */
  onConfirm: () => Promise<void>
  onExit: () => void
  /** The step's Write-ins / Kitchen / Delivery / Service cards, exactly as the desktop renders them. */
  renderStepCards: (step: RecordStep) => ReactNode
  /** Amending a signed record: Cancel instead of Save for later, Sign amendment instead of Confirm. */
  amend?: boolean
}

type Phase = 'choose' | 'upload' | 'reading' | 'error' | 'review'

/** Shared by every mount of the card and this wizard. */
const READING_BODY = 'This takes a few seconds.'

/** The value the keypad is on, and what it has typed so far. */
type Editing = TileRef & KeypadDraft

/** A row view with the band of the grade group it sits in, for the keypad's title. */
interface TileRow {
  view: RecordSpreadsheetRowView
  band: string | null
}

function tempCheckpointClass(value: RecordSpreadsheetRowView['temperatureClass']): TempCheckpointClass {
  switch (value) {
    case 'HOT':
      return 'hot'
    case 'CHILLED':
      return 'chilled'
    case 'AMBIENT':
      return 'ambient'
    default:
      return null
  }
}

export function MobileRecordWizard({
  subtitle,
  loading,
  busy,
  steps,
  stepDone,
  activeStepKey,
  onSelectStep,
  groupsFor,
  onScan,
  signGateOk,
  blockers,
  onConfirm,
  onExit,
  renderStepCards,
  amend = false,
}: MobileRecordWizardProps) {
  const [phase, setPhase] = useState<Phase>('choose')
  const [photos, setPhotos] = useState<SheetPhotos>(EMPTY_SHEET_PHOTOS)
  const [capMessage, setCapMessage] = useState<string | null>(null)
  const [summary, setSummary] = useState<ScanApplySummary | null>(null)
  const [scanError, setScanError] = useState<string | null>(null)
  /** The step under review; null before the first one. Advanced by `nextReviewStep` once it is signed. */
  const [reviewKey, setReviewKey] = useState<string | null>(null)
  const [editing, setEditing] = useState<Editing | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  const pending = reviewSteps(steps, stepDone)
  const current = phase === 'review' ? nextReviewStep(steps, stepDone, reviewKey) : null
  const currentKey = current?.key ?? null
  const photoCount = sheetPhotoCount(photos)

  // Entering a review step makes it the flow's ACTIVE step, so the flow's sign
  // chain and gates target it. Through a ref so the effect keys on the step
  // alone: the flow hands a new `selectStep` closure every render.
  const selectStepRef = useRef(onSelectStep)
  useEffect(() => {
    selectStepRef.current = onSelectStep
  })
  useEffect(() => {
    if (currentKey !== null) {
      setReviewKey(currentKey)
      setEditing(null)
      selectStepRef.current(currentKey)
    }
  }, [currentKey])

  // The value the keypad opened on comes into view above the pad.
  const editingRowKey = editing?.rowKey ?? null
  const editingColumn = editing?.column ?? null
  useEffect(() => {
    if (editingRowKey !== null && editingColumn !== null) {
      scrollToMarked(
        rootRef.current,
        `[data-record-tile-card="${attributeValue(editingRowKey)}"] [data-tile="${editingColumn}"]`,
      )
    }
  }, [editingRowKey, editingColumn])

  function addPhotos(paper: SheetPaper, incoming: File[]): void {
    const others = photoCount - photos[paper].length
    const merged = mergePhotos(photos[paper], incoming, Math.max(MOBILE_WIZARD_PHOTO_CAP - others, 0))
    setPhotos((prev) => ({ ...prev, [paper]: merged.photos }))
    setCapMessage(merged.capMessage ? `You can send up to ${MOBILE_WIZARD_PHOTO_CAP} photos at a time.` : null)
  }

  function removePhoto(paper: SheetPaper, key: string): void {
    setPhotos((prev) => ({ ...prev, [paper]: prev[paper].filter((file) => photoKey(file) !== key) }))
    setCapMessage(null)
  }

  function enterReview(): void {
    setReviewKey(activeStepKey)
    setPhase('review')
    setScanError(null)
  }

  async function startReading(): Promise<void> {
    const targets = reviewSteps(steps, stepDone)
    if (photoCount === 0 || targets.length === 0) {
      enterReview()
      return
    }
    setPhase('reading')
    setScanError(null)
    try {
      const result = await onScan(photos, targets)
      setSummary(result)
      // The photos are not kept once read (D22): the numbers are on the tiles now.
      setPhotos(EMPTY_SHEET_PHOTOS)
      setCapMessage(null)
      setPhase('review')
    } catch (err) {
      setScanError(err instanceof Error && err.message ? err.message : SCHOOL_SHEET_SCAN_COPY.errorFallback)
      setPhase('error')
    }
  }

  const header = (
    <CafeteriaWorkflowHeader
      title="Production record"
      subtitle={subtitle}
      actions={<SurfaceCloseButton onClick={onExit} disabled={busy} />}
    />
  )

  return (
    <div
      ref={rootRef}
      className="flex h-full min-h-0 flex-1 flex-col bg-background"
      data-layout="mobile-wizard"
      data-phase={phase}>
      {header}

      {loading ? (
        <main className="flex-1 space-y-4 px-4 pb-6 pt-5" aria-busy="true" aria-label="Loading production record">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-72" />
          <Skeleton className="h-[188px] w-full rounded-[14px]" />
          <Skeleton className="h-[188px] w-full rounded-[14px]" />
        </main>
      ) : steps.length === 0 ? (
        <main className="flex-1 px-4 pb-6 pt-5">
          <div className="rounded-lg border border-dashed bg-background p-6 text-center text-sm text-muted-foreground">
            No production records have been generated for this day yet.
          </div>
        </main>
      ) : pending.length === 0 ? (
        <main className="flex flex-1 flex-col items-center justify-center gap-4 px-4 pb-6 pt-5 text-center">
          <p className="text-sm text-muted-foreground">Every mealtime is signed.</p>
          <Button type="button" variant="outline" onClick={onExit}>
            Close
          </Button>
        </main>
      ) : phase === 'choose' ? (
        <main className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 pb-6 pt-6">
          <div>
            <h2 className="text-2xl font-semibold tracking-normal">How do you want to fill it in?</h2>
            <p className="mt-1 text-sm text-muted-foreground">{mealtimesLine(pending.map((step) => step.title))}</p>
          </div>
          <ChoiceButton
            icon={Camera}
            title="Scan the paper sheets"
            body="Take photos of the sheets you filled in by hand. Ordo reads the numbers, then you check them."
            onClick={() => setPhase('upload')}
          />
          <ChoiceButton
            icon={Keyboard}
            title="Type the numbers"
            body="Enter the counts and temperatures on your phone. No printing needed."
            onClick={enterReview}
          />
        </main>
      ) : phase === 'reading' ? (
        <main
          className="flex flex-1 flex-col items-center justify-center gap-3 px-4 pb-6 pt-5 text-center"
          aria-busy="true">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
          <p className="text-base font-medium">{readingTitle(photoCount)}</p>
          <p className="text-sm text-muted-foreground">{READING_BODY}</p>
        </main>
      ) : phase === 'error' ? (
        <>
          <main className="flex flex-1 flex-col items-center justify-center gap-3 px-4 pb-6 pt-5 text-center">
            <p className="text-base font-medium">Could not read the photos</p>
            <p className="text-sm text-muted-foreground">{scanError ?? SCHOOL_SHEET_SCAN_COPY.errorFallback}</p>
          </main>
          <div className="sticky bottom-0 flex gap-2 border-t bg-background p-4">
            <Button type="button" variant="outline" className="h-12 flex-1" onClick={() => setPhase('upload')}>
              Try again
            </Button>
            <Button type="button" className="h-12 flex-1" onClick={enterReview}>
              Review anyway
            </Button>
          </div>
        </>
      ) : phase === 'review' && current ? (
        <ReviewStep
          step={current}
          steps={steps}
          busy={busy}
          amend={amend}
          activeStepKey={activeStepKey}
          signGateOk={signGateOk}
          blockers={blockers}
          summary={summary}
          groups={groupsFor(current)}
          editing={editing}
          setEditing={setEditing}
          rootRef={rootRef}
          onConfirm={onConfirm}
          onExit={onExit}
          stepCards={renderStepCards(current)}
        />
      ) : (
        <>
          <main className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pb-6 pt-2">
            <button
              type="button"
              className="flex h-11 items-center gap-1 pr-2 text-sm font-medium text-muted-foreground"
              onClick={() => setPhase('choose')}>
              <ChevronLeft className="size-[18px]" aria-hidden="true" />
              Back
            </button>
            <div>
              <h2 className="text-2xl font-semibold tracking-normal">Add photos of your sheets</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                One upload for each paper. Add as many photos as you need.
              </p>
            </div>
            {pending.some((step) => step.trucks.length > 0) ? (
              <SheetUploadCard
                paper="delivery"
                photos={photos.delivery}
                onAdd={(files) => addPhotos('delivery', files)}
                onRemove={(key) => removePhoto('delivery', key)}
                note={deliverySheetNote(stepDeliveryDates(pending))}
                full={photoCount >= MOBILE_WIZARD_PHOTO_CAP}
              />
            ) : null}
            {pending.some((step) => step.productionRecordId !== null) ? (
              <SheetUploadCard
                paper="record"
                photos={photos.record}
                onAdd={(files) => addPhotos('record', files)}
                onRemove={(key) => removePhoto('record', key)}
                detail={recordSheetDetail(
                  pending.filter((step) => step.productionRecordId !== null).map((step) => step.title),
                )}
                full={photoCount >= MOBILE_WIZARD_PHOTO_CAP}
              />
            ) : null}
            {capMessage ? <p className="text-xs text-amber-700 dark:text-amber-500">{capMessage}</p> : null}
            <p className="text-[13px] leading-[19px] text-muted-foreground">
              Up to {MOBILE_WIZARD_PHOTO_CAP} photos in all. If one paper was not used, leave its upload empty.
            </p>
          </main>
          <div className="sticky bottom-0 flex flex-col gap-1 border-t bg-background p-4">
            <Button
              type="button"
              className="h-12 w-full text-base"
              disabled={photoCount === 0}
              onClick={() => void startReading()}>
              {readPhotosLabel(photoCount)}
              <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
            </Button>
            <Button type="button" variant="link" className="h-11 w-full text-[15px]" onClick={enterReview}>
              Type the numbers instead
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

/** One of the start screen's two equal choices: a big icon, a title, one line. */
function ChoiceButton({
  icon: Icon,
  title,
  body,
  onClick,
}: {
  icon: LucideIcon
  title: string
  body: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-[188px] w-full flex-col items-start gap-3.5 rounded-[14px] border bg-card p-5 text-left shadow-xs transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <span className="flex size-[52px] items-center justify-center rounded-[14px] bg-primary/10 text-primary">
        <Icon className="size-[26px]" aria-hidden="true" />
      </span>
      <span className="flex flex-col gap-1.5">
        <span className="text-lg font-semibold leading-6">{title}</span>
        <span className="text-sm leading-[21px] text-muted-foreground">{body}</span>
      </span>
    </button>
  )
}

interface ReviewStepProps {
  step: RecordStep
  steps: readonly RecordStep[]
  busy: boolean
  amend: boolean
  activeStepKey: string | null
  signGateOk: boolean
  blockers: readonly StepBlocker[]
  summary: ScanApplySummary | null
  groups: readonly RecordSpreadsheetGroupView[]
  editing: Editing | null
  setEditing: (next: Editing | null) => void
  rootRef: RefObject<HTMLDivElement | null>
  onConfirm: () => Promise<void>
  onExit: () => void
  stepCards: ReactNode
}

/** One mealtime under review: what blocks it, its rows as tiles, its cards, and the one main button. */
function ReviewStep({
  step,
  steps,
  busy,
  amend,
  activeStepKey,
  signGateOk,
  blockers,
  summary,
  groups,
  editing,
  setEditing,
  rootRef,
  onConfirm,
  onExit,
  stepCards,
}: ReviewStepProps) {
  const tileRows: TileRow[] = groups.flatMap((group) =>
    group.sections.flatMap((section) =>
      section.rows.map((view) => ({ view, band: group.label === 'All grades' ? null : group.label })),
    ),
  )
  const tiles = editableTiles(tileRows.map((row) => row.view))
  const editingRow = editing ? (tileRows.find((row) => row.view.key === editing.rowKey) ?? null) : null
  const editingCell = editing && editingRow ? editingRow.view.cells[editing.column] : null
  // The list belongs to the ACTIVE step; it is this one once the selection effect has run.
  const live = activeStepKey === step.key
  const blocked = live && blockers.length > 0

  function open(rowKey: string, column: RecordColumnKey): void {
    const row = tileRows.find((candidate) => candidate.view.key === rowKey)
    const cell = row?.view.cells[column]
    if (!cell || cell.disabled) {
      return
    }
    setEditing({ rowKey, column, text: tileText(cell.value), fresh: true })
  }

  function fix(blocker: StepBlocker | undefined): void {
    if (!blocker) {
      return
    }
    const target = blocker.target
    if (target?.kind === 'cell') {
      open(target.rowKey, target.column)
      return
    }
    setEditing(null)
    if (target?.kind === 'card') {
      scrollToMarked(rootRef.current, `[data-step-card="${target.card}"]`)
      return
    }
    scrollToMarked(rootRef.current, '[data-step-blockers]')
  }

  function press(key: NumericKeypadKey): void {
    if (!editing || !editingCell || editingCell.disabled) {
      return
    }
    const decimal = editingCell.kind === 'temp'
    const next = pressKeypadKey(editing, key, decimal)
    setEditing({ ...editing, ...next })
    if (editingCell.kind === 'count') {
      editingCell.onValueChange(next.text)
    } else {
      editingCell.onChange(draftTemperature(next.text))
    }
  }

  const presets =
    editingCell?.kind === 'temp' && editingRow
      ? presetOptionsForClass(tempCheckpointClass(editingRow.view.temperatureClass))
      : []
  const prev = editing ? adjacentTile(tiles, editing, -1) : null
  const next = editing ? adjacentTile(tiles, editing, 1) : null
  const nextRow = next ? tileRows.find((row) => row.view.key === next.rowKey) : null

  return (
    <>
      <main className={`min-h-0 flex-1 overflow-y-auto px-4 pt-5 ${editing ? 'pb-[460px]' : 'pb-6'}`}>
        <h2 className="text-2xl font-semibold tracking-normal">Review {step.title}</h2>
        <p className="mt-1 text-xs text-muted-foreground">{stepCounter(steps, step.key)}</p>

        {summary ? <ScanSummaryLine summary={summary} /> : null}

        {blocked ? (
          <section
            data-step-blockers
            aria-label={blockersTitle(blockers.length)}
            className="mt-4 flex flex-col rounded-xl border p-3.5">
            <div className="flex items-center gap-2 pb-1.5">
              <CircleAlert className="size-5 shrink-0 text-destructive" aria-hidden="true" />
              <h3 className="text-[15px] font-semibold leading-[21px]">{blockersTitle(blockers.length)}</h3>
            </div>
            <ul>
              {blockers.map((blocker) => (
                <li key={blocker.key}>
                  <button
                    type="button"
                    className="flex min-h-12 w-full items-center gap-2 border-t py-2 text-left text-sm leading-5"
                    onClick={() => fix(blocker)}>
                    <span className="min-w-0 flex-1">{blocker.text}</span>
                    <ChevronRight className="size-[18px] shrink-0 text-muted-foreground" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {tileRows.length === 0 ? (
          <p className="mt-4 rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
            No items on this record.
          </p>
        ) : (
          <div className="mt-4 space-y-5">
            {/* The desktop's layout (PROD-1441): every grade group titled the same way, its sections, one card
                per counting row, and its claim in the same card as the desktop's. */}
            {groups.map((group) => (
              <section key={group.key} data-grade-group={group.label} className="space-y-3">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <h3 className="text-base font-semibold leading-6">{gradeGroupTitle(group.label)}</h3>
                  {group.pattern ? <span className="text-xs text-muted-foreground">{group.pattern}</span> : null}
                </div>
                {group.sections.map((section) => (
                  <div key={section.key} data-section={section.key} className="space-y-3">
                    <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {section.title}
                    </h4>
                    {section.rows.map((rowView) => (
                      <RecordTileCard
                        key={rowView.key}
                        row={rowView}
                        activeColumn={editing?.rowKey === rowView.key ? editing.column : null}
                        onEdit={(column) => open(rowView.key, column)}
                      />
                    ))}
                  </div>
                ))}
                {group.claimed ? <ClaimedMealsCard claimed={group.claimed} /> : null}
              </section>
            ))}
          </div>
        )}

        {stepCards}
      </main>
      <div className="sticky bottom-0 flex gap-2 border-t bg-background p-4">
        <Button type="button" variant="outline" className="h-12" onClick={onExit} disabled={busy}>
          {amend ? 'Cancel' : 'Save for later'}
        </Button>
        {blocked ? (
          <Button type="button" className="h-12 flex-1" disabled={busy} onClick={() => fix(blockers[0])}>
            {fixLabel(blockers.length)}
            <ArrowDown className="ml-1.5 h-4 w-4" aria-hidden="true" />
          </Button>
        ) : (
          <Button
            type="button"
            className="h-12 flex-1"
            disabled={busy || !signGateOk || !live}
            onClick={() => void onConfirm()}>
            {amend
              ? busy
                ? 'Signing…'
                : 'Sign amendment'
              : confirmLabel({ hasRecord: step.productionRecordId !== null, busy, title: step.title })}
          </Button>
        )}
      </div>
      {editing && editingRow && editingCell ? (
        <DockedKeypad
          title={`${editingRow.band ? `${editingRow.view.label} ${editingRow.band}` : editingRow.view.label} · ${TILE_LABEL[editing.column]}`}
          subtitle={keypadHint(
            editing.column,
            (column) => editingRow.view.cells[column].value,
            editingRow.view.temperatureClass,
          )}
          decimal={editingCell.kind === 'temp'}
          presets={presets.map((preset) => ({
            key: preset.key,
            label:
              preset.temperatureF === null ? preset.label : `${preset.label} · ${preset.temperatureF.toFixed(1)}°F`,
          }))}
          activePresetKey={
            editingCell.kind === 'temp'
              ? (presets.find((preset) => preset.temperatureF === editingCell.value)?.key ?? null)
              : null
          }
          onPreset={(key) => {
            const preset = presets.find((candidate) => candidate.key === key)
            if (!preset || editingCell.kind !== 'temp') {
              return
            }
            editingCell.onChange(preset.temperatureF)
            setEditing({ ...editing, text: tileText(preset.temperatureF), fresh: true })
          }}
          onKey={press}
          onPrev={prev ? () => open(prev.rowKey, prev.column) : null}
          onNext={next ? () => open(next.rowKey, next.column) : null}
          nextName={next && nextRow ? `${nextRow.view.label} ${TILE_LABEL[next.column]}` : null}
          onDone={() => setEditing(null)}
        />
      ) : null}
    </>
  )
}

/** What the photos filled, in the scan card's own words, plus what needs a second look. */
function ScanSummaryLine({ summary }: { summary: ScanApplySummary }) {
  const needsReview =
    summary.lowConfidenceCount > 0 || summary.warnings.length > 0 || summary.unmatchedRowsSeen.length > 0
  return (
    <div className="mt-3 rounded-lg border bg-muted/30 p-3 text-sm">
      <p>
        Filled {summary.filledCount} of {summary.totalCount} {SCHOOL_SHEET_SCAN_COPY.filledUnit}. Tap a value to fix it.
      </p>
      {needsReview ? (
        <div className="mt-1 space-y-0.5 text-xs text-amber-700 dark:text-amber-500">
          {summary.lowConfidenceCount > 0 ? (
            <p>
              {summary.lowConfidenceCount === 1
                ? '1 value needs a second look.'
                : `${summary.lowConfidenceCount} values need a second look.`}
            </p>
          ) : null}
          {summary.warnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
          {summary.unmatchedRowsSeen.length > 0 ? (
            <p>We saw rows on the sheet we could not place: {summary.unmatchedRowsSeen.join(', ')}.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
